/**
 * Settings → Pressure Control, BOP-2 cards (api#1281), each with its own Save:
 *
 *   PcDrillSettingsCard  — the crew-drill warning interval (1 to 365 days,
 *                          default 7). A warning only; a drill never blocks.
 *   PcTestDefaultsCard   — default test pressures and holds per component
 *                          class, or per component (which wins over its class).
 *                          They pre-fill a new pressure test's rows; the hand
 *                          can change any figure on the test.
 *
 * A PUT of a default REPLACES it: a field left blank is cleared, not kept. The
 * dialog starts from the stored figures so an edit changes only what is typed.
 * A default is a setting, not a record, so Remove is offered (DELETE).
 * Pressures are in the tenant's unit (the API converts); holds in minutes.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { LoadFailureNotice } from '../ui/LoadFailureNotice';
import { PcErrorLine } from './PcSectionNav';
import {
  useDeletePcTestDefault,
  usePcComponents,
  usePcDrillSettings,
  usePcTestDefaults,
  usePutPcTestDefault,
  useUpdatePcDrillSettings,
} from '../../hooks/usePressureControl';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { PC_CLASS_LABEL, pcClassLabel, pcErrorMessage, pcMinutes, pcPressure } from '../../lib/pressure-control';
import type { PcTestDefault, PcTestDefaultWrite } from '../../types/pressure-control';
import { DH_INPUT, DH_SECONDARY, DhDialog, DhField, useOpenCount } from '../../pages/downhole/DownholeBits';

const inputCls =
  'w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2.5 text-white text-sm placeholder-fieldiq-muted focus:outline-none focus:border-fieldiq-accent/50 disabled:opacity-60 disabled:cursor-not-allowed';

function SaveButton({ dirty, pending, onClick, label }: { dirty: boolean; pending: boolean; onClick: () => void; label: string }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      disabled={!dirty || pending}
      className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
        dirty ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'bg-fieldiq-border text-fieldiq-muted cursor-not-allowed'
      }`}
    >
      {pending ? <Loader2 size={16} className="animate-spin" /> : null}
      {pending ? 'Saving...' : 'Save'}
    </button>
  );
}

/** The drill interval as a PUT value, or an error sentence. */
export function pcDrillDaysValue(text: string): number | string {
  const t = text.trim();
  if (!/^\d+$/.test(t) || Number(t) < 1 || Number(t) > 365) return 'Drill interval must be a whole number of days from 1 to 365.';
  return Number(t);
}

export function PcDrillSettingsCard({ canWrite }: { canWrite: boolean }) {
  const query = usePcDrillSettings();
  const save = useUpdatePcDrillSettings();
  const failure = queryLoadFailure(query);
  const s = query.data ?? null;
  const [text, setText] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (s && text === null) setText(String(s.drillWarnDays));
  }, [s, text]);

  const value = text === null ? null : pcDrillDaysValue(text);
  const dirty = !!s && text !== null && (typeof value === 'string' || value !== s.drillWarnDays);

  const onSave = () => {
    setError(null);
    if (typeof value === 'string') return setError(value);
    if (value === null) return;
    save.mutate({ drill_warn_days: value }, {
      onSuccess: (saved) => {
        if (saved) setText(String(saved.drillWarnDays));
        toast.success('Drill interval saved');
      },
      onError: (e) => setError(pcErrorMessage(e, 'The drill interval could not be saved.')),
    });
  };

  return (
    <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5 space-y-4" data-testid="pc-drill-settings">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-white">Crew BOP drills</h3>
          <p className="mt-1 text-sm text-fieldiq-text">
            A crew member, rig or stack with no drill in this many days is flagged. It is a warning only; nothing is blocked.
          </p>
          {s && !s.stored && <p className="mt-1 text-sm text-fieldiq-muted">Nothing has been saved yet, so this is the standard default.</p>}
        </div>
        {canWrite && s && <SaveButton dirty={dirty} pending={save.isPending} onClick={onSave} label="Save drill interval" />}
      </div>
      {failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="drill settings" />
      ) : query.isLoading || text === null ? (
        <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading drill settings" />
      ) : (
        <label className="block max-w-xs">
          <span className="mb-1.5 block text-xs font-medium text-fieldiq-text">Drill interval (days)</span>
          <input className={inputCls} inputMode="numeric" value={text} disabled={!canWrite} aria-label="Drill interval" onChange={(e) => setText(e.target.value)} />
        </label>
      )}
      <PcErrorLine message={error} />
    </div>
  );
}

const NUM = /^\d+(\.\d+)?$/;
const INT = /^\d+$/;

export interface DefaultDraft {
  target: 'class' | 'component';
  componentClass: string;
  equipmentId: string;
  low: string;
  lowHold: string;
  high: string;
  highHold: string;
}

/** The PUT body for a default, or an error sentence. Blank fields are sent as null (a PUT replaces). */
export function pcTestDefaultBody(d: DefaultDraft, unit: string): PcTestDefaultWrite | string {
  const body: PcTestDefaultWrite = {};
  if (d.target === 'class') {
    if (!d.componentClass) return 'Choose the component class.';
    body.component_class = d.componentClass;
  } else {
    if (!d.equipmentId) return 'Choose the component.';
    body.equipment_id = d.equipmentId;
  }
  const num = (v: string, label: string): number | null | string => {
    const t = v.trim().replace(/,/g, '');
    if (!t) return null;
    if (!NUM.test(t) || Number(t) <= 0) return `${label} must be a number greater than 0 (${unit}).`;
    return Number(t);
  };
  const mins = (v: string, label: string): number | null | string => {
    const t = v.trim();
    if (!t) return null;
    if (!INT.test(t) || Number(t) < 1 || Number(t) > 240) return `${label} must be whole minutes from 1 to 240.`;
    return Number(t);
  };
  const low = num(d.low, 'Low test pressure');
  if (typeof low === 'string') return low;
  const high = num(d.high, 'High test pressure');
  if (typeof high === 'string') return high;
  if (low !== null && high !== null && low >= high) return 'The low test pressure must be below the high test pressure.';
  const lowHold = mins(d.lowHold, 'Low hold');
  if (typeof lowHold === 'string') return lowHold;
  const highHold = mins(d.highHold, 'High hold');
  if (typeof highHold === 'string') return highHold;
  if (low === null && high === null && lowHold === null && highHold === null) return 'Give at least one figure.';
  body.low_test_pressure = low;
  body.high_test_pressure = high;
  body.low_hold_minutes = lowHold;
  body.high_hold_minutes = highHold;
  return body;
}

const draftOf = (d: PcTestDefault | null): DefaultDraft => ({
  target: d?.equipmentId ? 'component' : 'class',
  componentClass: d?.componentClass ?? '',
  equipmentId: d?.equipmentId ?? '',
  low: d?.lowTestPressure != null ? String(d.lowTestPressure) : '',
  lowHold: d?.lowHoldMinutes != null ? String(d.lowHoldMinutes) : '',
  high: d?.highTestPressure != null ? String(d.highTestPressure) : '',
  highHold: d?.highHoldMinutes != null ? String(d.highHoldMinutes) : '',
});

function DefaultDialog({
  open, onClose, editing, unit,
}: { open: boolean; onClose: () => void; editing: PcTestDefault | null; unit: string }) {
  const put = usePutPcTestDefault();
  const openCount = useOpenCount(open);
  const components = usePcComponents(open && !editing);
  const [d, setD] = useState<DefaultDraft>(draftOf(null));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setD(draftOf(editing));
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, openCount]);

  const submit = async () => {
    setError(null);
    const body = pcTestDefaultBody(d, unit);
    if (typeof body === 'string') return setError(body);
    try {
      await put.mutateAsync(body);
      toast.success('Default saved');
      onClose();
    } catch (e) {
      setError(pcErrorMessage(e, 'The default could not be saved.'));
    }
  };
  const set = (k: keyof DefaultDraft) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setD({ ...d, [k]: e.target.value });

  return (
    <DhDialog
      open={open}
      onClose={onClose}
      title={editing ? 'Edit default test pressures' : 'Add default test pressures'}
      footer={
        <>
          <button type="button" className={DH_SECONDARY} onClick={onClose}>Cancel</button>
          <button type="button" onClick={submit} disabled={put.isPending} className="fi-action fi-action--standard fi-action--primary">
            {put.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Save default
          </button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        {editing ? (
          <p className="sm:col-span-2 text-sm text-fieldiq-text">
            For {editing.equipmentId ? (editing.equipmentName ?? '—') : `every ${pcClassLabel(editing.componentClass)}`}. A blank figure is cleared.
          </p>
        ) : (
          <>
            <DhField label="Applies to">
              <select className={DH_INPUT} value={d.target} onChange={set('target')} aria-label="Applies to">
                <option value="class">A component class</option>
                <option value="component">One component</option>
              </select>
            </DhField>
            {d.target === 'class' ? (
              <DhField label="Class">
                <select className={DH_INPUT} value={d.componentClass} onChange={set('componentClass')} aria-label="Class">
                  <option value="">Choose…</option>
                  {Object.entries(PC_CLASS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </DhField>
            ) : (
              <DhField label="Component" hint="Wins over its class's default.">
                <select className={DH_INPUT} value={d.equipmentId} onChange={set('equipmentId')} aria-label="Component">
                  <option value="">Choose…</option>
                  {(components.data?.rows ?? []).map((c) => <option key={c.equipmentId} value={c.equipmentId}>{c.name}</option>)}
                </select>
              </DhField>
            )}
          </>
        )}
        <DhField label={`Low test (${unit})`}>
          <input className={DH_INPUT} inputMode="decimal" value={d.low} onChange={set('low')} aria-label="Low test pressure" />
        </DhField>
        <DhField label="Low hold (minutes)">
          <input className={DH_INPUT} inputMode="numeric" value={d.lowHold} onChange={set('lowHold')} aria-label="Low hold" />
        </DhField>
        <DhField label={`High test (${unit})`}>
          <input className={DH_INPUT} inputMode="decimal" value={d.high} onChange={set('high')} aria-label="High test pressure" />
        </DhField>
        <DhField label="High hold (minutes)">
          <input className={DH_INPUT} inputMode="numeric" value={d.highHold} onChange={set('highHold')} aria-label="High hold" />
        </DhField>
      </div>
      <PcErrorLine message={error} />
    </DhDialog>
  );
}

export function PcTestDefaultsCard({ canWrite }: { canWrite: boolean }) {
  const query = usePcTestDefaults();
  const meta = usePcComponents();
  const remove = useDeletePcTestDefault();
  const failure = queryLoadFailure(query);
  const rows = query.data ?? [];
  const unit = meta.data?.meta?.pressureUnit ?? rows[0]?.pressureUnit ?? '';
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PcTestDefault | null>(null);

  const onRemove = (d: PcTestDefault) => {
    remove.mutate(d.id, {
      onSuccess: () => toast.success('Default removed'),
      onError: (e) => toast.error(pcErrorMessage(e, 'The default could not be removed.')),
    });
  };
  const target = (d: PcTestDefault) => (d.equipmentId ? (d.equipmentName ?? '—') : `Every ${pcClassLabel(d.componentClass)}`);

  return (
    <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5 space-y-4" data-testid="pc-test-defaults">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-white">Default test pressures</h3>
          <p className="mt-1 text-sm text-fieldiq-text">
            Pre-fill a new pressure test&rsquo;s rows. A component&rsquo;s own default wins over its class&rsquo;s. The figures on a test can always be changed.
          </p>
          {!canWrite && <p className="mt-1 text-sm text-fieldiq-muted">Only an admin or office staff can change these.</p>}
        </div>
        {canWrite && (
          <button type="button" onClick={() => { setEditing(null); setOpen(true); }} className={DH_SECONDARY}>
            <Plus className="size-4" aria-hidden="true" />
            Add default
          </button>
        )}
      </div>
      {failure ? (
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="default test pressures" />
      ) : query.isLoading ? (
        <Loader2 className="w-5 h-5 text-fieldiq-accent animate-spin" aria-label="Loading default test pressures" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-fieldiq-text">No defaults set. A new test starts with blank pressures.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left">
            <thead className="border-b border-fieldiq-border text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
              <tr>
                <th scope="col" className="px-3 py-2">For</th>
                <th scope="col" className="px-3 py-2">Low test</th>
                <th scope="col" className="px-3 py-2">Low hold</th>
                <th scope="col" className="px-3 py-2">High test</th>
                <th scope="col" className="px-3 py-2">High hold</th>
                {canWrite && <th scope="col" className="px-3 py-2" />}
              </tr>
            </thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="border-t border-fieldiq-border" data-testid="pc-test-default-row">
                  <td className="px-3 py-2 text-sm font-semibold text-white">{target(d)}</td>
                  <td className="px-3 py-2 font-mono text-sm tabular-nums text-fieldiq-text">{pcPressure(d.lowTestPressure, d.pressureUnit)}</td>
                  <td className="px-3 py-2 font-mono text-sm tabular-nums text-fieldiq-text">{pcMinutes(d.lowHoldMinutes)}</td>
                  <td className="px-3 py-2 font-mono text-sm tabular-nums text-fieldiq-text">{pcPressure(d.highTestPressure, d.pressureUnit)}</td>
                  <td className="px-3 py-2 font-mono text-sm tabular-nums text-fieldiq-text">{pcMinutes(d.highHoldMinutes)}</td>
                  {canWrite && (
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => { setEditing(d); setOpen(true); }} className={DH_SECONDARY} aria-label={`Edit default for ${target(d)}`}>
                          <Pencil className="size-4" aria-hidden="true" />
                          Edit
                        </button>
                        <button type="button" onClick={() => onRemove(d)} disabled={remove.isPending} className={DH_SECONDARY} aria-label={`Remove default for ${target(d)}`}>
                          <Trash2 className="size-4" aria-hidden="true" />
                          Remove
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {canWrite && <DefaultDialog open={open} onClose={() => setOpen(false)} editing={editing} unit={unit} />}
    </div>
  );
}
